import { getToken } from '../token';

// Types for Metabase API responses
export interface MetabaseRow {
  [key: string]: any;
}

export interface MetabaseColumn {
  display_name: string;
  source: string;
  field_ref: any[];
  name: string;
  base_type: string;
  effective_type: string;
}

export interface MetabaseData {
  rows: MetabaseRow[];
  cols: MetabaseColumn[];
  insights: any;
  results_timezone: string;
}

export interface MetabaseResponse {
  data: MetabaseData;
  json_query: {
    parameters: Array<{
      type: string;
      target: any[];
      slug: string;
      value: string;
    }>;
  };
  status: string;
}

export interface MetabaseQuestionRequest {
  dashboard_type: string;
  id: number;
  token: string;
}

export interface MetabaseQuestionResponse {
  get_response_forwarder_to_metabase_api: string;
}

/** Granularidad temporal con la que Metabase agrupa una serie. */
export type MetabaseGroupByTypeDate = 'hours' | 'day' | 'month';

export interface MetabaseIframeParams {
  /** Ids de grupos de avatares, ya extraídos de sus URIs. Vacío = sin filtro. */
  groupIds: string[];
  groupByTypeDate: MetabaseGroupByTypeDate;
  creationDateAfter: string;
  creationDateBefore: string;
}

// Type for ANC Next Maintenance or Emptying data (Question 190)
export interface ANCNextMaintenanceOrEmptying {
  id: string;
  resource: string;
  resource_id: string;
  threshold_id: string;
  execution_date: string;
  status: string;
  created_at: string;
  updated_at: string;
  threshold_condition_id: string;
  company_id: string;
  avatar_id: string;
  threshold_type: string;
}

// Metabase service class
class MetabaseService {
  private static instance: MetabaseService;
  private readonly baseUrl = 'https://backend001.360sc.yt/bridgeMetabase/api/views/viewsdynamic';

  private constructor() {}

  public static getInstance(): MetabaseService {
    if (!MetabaseService.instance) {
      MetabaseService.instance = new MetabaseService();
    }
    return MetabaseService.instance;
  }

  /**
   * Get data from a Metabase question
   * @param questionId - The ID of the Metabase question
   * @param token - Optional token, if not provided will use the current user's token
   * @returns Promise<MetabaseResponse>
   */
  public async getQuestionData(questionId: number, token?: string): Promise<MetabaseResponse> {
    const authToken = token || getToken();
    
    if (!authToken) {
      throw new Error('No authentication token available');
    }

    // Step 1: Get the forwarder URL
    const forwarderUrl = await this.getForwarderUrl(questionId, authToken);
    
    // Step 2: Get the actual data from the forwarder URL
    const response = await fetch(forwarderUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch Metabase data: ${response.status} ${response.statusText}`);
    }

    const data: MetabaseResponse = await response.json();
    return data;
  }

  /**
   * Obtiene la URL firmada del iframe de una question de Metabase.
   *
   * El mismo endpoint que `getForwarderUrl` devuelve además `iframeURL`, que es
   * lo que el backoffice platform incrusta. El bridge decodifica el JWT y añade
   * `id_company` a los parámetros que firma, así que la question ya llega
   * acotada a la compañía del usuario.
   *
   * Si falta `iframeURL`, fallamos con un mensaje explícito en vez de dejar un
   * iframe en blanco.
   */
  public async getQuestionIframeUrl(
    questionId: number,
    params: MetabaseIframeParams,
    token?: string,
  ): Promise<string> {
    const authToken = token || getToken();

    if (!authToken) {
      throw new Error('No authentication token available');
    }

    const body = {
      token: authToken,
      id: questionId,
      dashboard_type: 'question',
      also_children: 'true',
      group_ids: params.groupIds.length > 0 ? params.groupIds.join(',') : null,
      metadata_label_business_id: null,
      metadata_label_constuction_site_id: null,
      metadata_label_factory_id: null,
      groupByTypeDate: params.groupByTypeDate,
      creation_date_after: params.creationDateAfter,
      creation_date_before: params.creationDateBefore,
    };

    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`Failed to get Metabase iframe URL: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    if (!data || typeof data.iframeURL !== 'string') {
      throw new Error(`Metabase question ${questionId} did not return an iframeURL`);
    }

    return data.iframeURL;
  }

  /**
   * Get the forwarder URL for a Metabase question
   * @param questionId - The ID of the Metabase question
   * @param token - Authentication token
   * @returns Promise<string> - The forwarder URL
   */
  private async getForwarderUrl(questionId: number, token: string): Promise<string> {
    const requestBody: MetabaseQuestionRequest = {
      dashboard_type: 'question',
      id: questionId,
      token: token,
    };

    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`Failed to get forwarder URL: ${response.status} ${response.statusText}`);
    }

    const data: MetabaseQuestionResponse = await response.json();
    return data.get_response_forwarder_to_metabase_api;
  }

  /**
   * Extract total ANCs value from Metabase response
   * @param response - The Metabase response
   * @returns number | null - The total ANCs value or null if not found
   */
  public getTotalANCs(response: MetabaseResponse): number | null {
    const { rows } = response.data;
    
    // Find the row with "Total" status
    const totalRow = rows.find(row => {
      // Assuming the status is in the second column (index 1)
      return row[1] === 'Total';
    });

    if (totalRow) {
      // Assuming the count is in the first column (index 0)
      return totalRow[0] || null;
    }

    return null;
  }

  /**
   * Get all status data from Metabase response
   * @param response - The Metabase response
   * @returns Array<{count: number | null, status: string}> - Array of status data
   */
  public getStatusData(response: MetabaseResponse): Array<{count: number | null, status: string}> {
    const { rows } = response.data;
    
    return rows.map(row => ({
      count: row[0] || null,
      status: row[1] || '',
    }));
  }

  /**
   * Get specific question 187 data (Total ANCs)
   * @param token - Optional token, if not provided will use the current user's token
   * @returns Promise<number | null> - The total ANCs value
   */
  public async getTotalANCsFromQuestion187(token?: string): Promise<number | null> {
    try {
      const response = await this.getQuestionData(187, token);
      const { rows } = response.data;
      
      // Find the row with "Total" status
      const totalRow = rows.find(row => {
        // Assuming the status is in the second column (index 1)
        return row[1] === 'Total';
      });

      if (totalRow) {
        // Assuming the count is in the first column (index 0)
        return totalRow[0] || null;
      }

      return null;
    } catch (error) {
      console.error('Error fetching total ANCs from question 187:', error);
      throw error;
    }
  }

  /**
   * Get threshold data from question 188
   * @param token - Optional token, if not provided will use the current user's token
   * @returns Promise<{maintenance: {[key: string]: number}, vidange: {[key: string]: number}}>
   */
  public async getThresholdDataFromQuestion188(token?: string): Promise<{
    maintenance: {[key: string]: number};
    vidange: {[key: string]: number};
  }> {
    try {
      const response = await this.getQuestionData(188, token);
      const { rows } = response.data;
      
      // Initialize the result structure
      const result = {
        maintenance: {} as {[key: string]: number},
        vidange: {} as {[key: string]: number}
      };

      // Process each row: [counter, threshold_name, as_type]
      rows.forEach(row => {
        const counter = row[0] || 0;
        const thresholdName = row[1] || 'unknown';
        const asType = row[2] || 'unknown';

        if (asType === 'maintenance') {
          result.maintenance[thresholdName] = counter;
        } else if (asType === 'vidange') {
          result.vidange[thresholdName] = counter;
        }
      });

      return result;
    } catch (error) {
      console.error('Error fetching threshold data from question 188:', error);
      throw error;
    }
  }

  /**
   * Get ANC Next Maintenance or Emptying data from question 190
   * @param token - Optional token, if not provided will use the current user's token
   * @returns Promise<ANCNextMaintenanceOrEmptying[]> - Array of ANC maintenance/emptying data
   */
  public async getANCNextMaintenanceOrEmptyingFromQuestion190(token?: string): Promise<ANCNextMaintenanceOrEmptying[]> {
    try {
      const response = await this.getQuestionData(190, token);
      const { rows, cols } = response.data;
      
      // Map column names to indices for easier access
      const columnMap: {[key: string]: number} = {};
      cols.forEach((col, index) => {
        columnMap[col.name] = index;
      });

      // Transform rows into typed objects
      const result: ANCNextMaintenanceOrEmptying[] = rows.map(row => ({
        id: row[columnMap.id] || '',
        resource: row[columnMap.resource] || '',
        resource_id: row[columnMap.resource_id] || '',
        threshold_id: row[columnMap.threshold_id] || '',
        execution_date: row[columnMap.execution_date] || '',
        status: row[columnMap.status] || '',
        created_at: row[columnMap.created_at] || '',
        updated_at: row[columnMap.updated_at] || '',
        threshold_condition_id: row[columnMap.threshold_condition_id] || '',
        company_id: row[columnMap.company_id] || '',
        avatar_id: row[columnMap.avatar_id] || '',
        threshold_type: row[columnMap.threshold_type] || '',
      }));

      return result;
    } catch (error) {
      console.error('Error fetching ANC Next Maintenance or Emptying data from question 190:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const metabaseService = MetabaseService.getInstance();

// Export convenience functions
export const getMetabaseQuestionData = (questionId: number, token?: string) => 
  metabaseService.getQuestionData(questionId, token);

export const getTotalANCs = (response: MetabaseResponse) => 
  metabaseService.getTotalANCs(response);

export const getStatusData = (response: MetabaseResponse) => 
  metabaseService.getStatusData(response);

export const getTotalANCsFromQuestion187 = (token?: string) => 
  metabaseService.getTotalANCsFromQuestion187(token);

export const getThresholdDataFromQuestion188 = (token?: string) => 
  metabaseService.getThresholdDataFromQuestion188(token);

export const getANCNextMaintenanceOrEmptyingFromQuestion190 = (token?: string) => 
  metabaseService.getANCNextMaintenanceOrEmptyingFromQuestion190(token);

export default metabaseService;
